import { Modal, TextInput } from '@tocktix/pantry';
import { useEffect, useRef, useState } from 'react';

interface Props {
  open: boolean;
  /** Editing an existing link (shows "Edit link" + a Remove action) vs. adding a new one. */
  isEditing: boolean;
  /** Prefill value, captured when the modal opens (the current link's href, or ''). */
  initialUrl: string;
  /** Receives the normalized href; an empty string signals the link should be removed. */
  onApply: (href: string) => void;
  onRemove: () => void;
  onClose: () => void;
}

/**
 * Normalizes a user-entered link target so it matches a protocol the legal HTML
 * sanitizer permits (`https?:`/`mailto:`). A bare domain (no scheme) gets an
 * `https://` prefix; anything already carrying a scheme is left as typed.
 */
export const normalizeLinkHref = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
};

/**
 * URL entry modal for the legal rich-text editor's link controls. Purely
 * presentational: it owns the input value and focus, but defers the actual
 * TipTap commands to its parent via `onApply`/`onRemove`.
 */
const LinkEditorModal: React.FunctionComponent<Props> = ({
  open,
  isEditing,
  initialUrl,
  onApply,
  onRemove,
  onClose,
}) => {
  const [urlValue, setUrlValue] = useState(initialUrl);
  const urlInputRef = useRef<HTMLInputElement | null>(null);

  // Reset to the current link and move focus to the field each time the modal
  // opens. The pantry Modal's FocusTrap focuses the header close button first,
  // so this claims focus for the input afterward.
  useEffect(() => {
    if (open) {
      setUrlValue(initialUrl);
      urlInputRef.current?.focus();
    }
  }, [open, initialUrl]);

  const handleApply = () => onApply(normalizeLinkHref(urlValue));

  return (
    <Modal id="legal-editor-link-modal" open={open} onClose={onClose} size="sm">
      <Modal.Header title={isEditing ? 'Edit link' : 'Add link'} />
      <Modal.Body>
        <TextInput
          ref={urlInputRef}
          id="legal-editor-link-url"
          name="url"
          label="URL"
          placeholder="https://exploretock.com/privacy"
          value={urlValue}
          autoFocus
          fullWidth
          onChange={(event) => setUrlValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleApply();
            }
          }}
        />
      </Modal.Body>
      <Modal.Footer
        actions={{
          primary: {
            label: 'Apply',
            onClick: handleApply,
          },
          ...(isEditing
            ? { secondary: { label: 'Remove link', danger: true, onClick: onRemove } }
            : {}),
          cancel: {
            label: 'Cancel',
            onClick: onClose,
          },
        }}
      />
    </Modal>
  );
};

export default LinkEditorModal;
